"use client";

import React from "react";
import Link from "next/link";
import { Check, X, AlertTriangle, ArrowRight } from "lucide-react";

type Status = "yes" | "no" | "limited";

type QuickCompareProps = {
  tool: string;
  rows: {
    feature: string;
    tool: Status;
    tavlo: Status;
  }[];
};

function StatusIcon({ status }: { status: Status }) {
  if (status === "yes") {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 shrink-0">
        <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
      </span>
    );
  }
  if (status === "limited") {
    return (
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500/15 shrink-0">
        <AlertTriangle className="w-3 h-3 text-amber-400" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500/10 shrink-0">
      <X className="w-3 h-3 text-red-400/70" strokeWidth={3} />
    </span>
  );
}

function statusLabel(status: Status) {
  if (status === "yes") return "Yes";
  if (status === "limited") return "Limited";
  return "No";
}

function DesktopTable({ tool, rows }: QuickCompareProps) {
  return (
    <div className="hidden md:block overflow-hidden rounded-xl border border-white/[0.06]">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="bg-white/[0.03]">
            <th className="text-left py-3.5 px-5 text-[11px] font-bold uppercase tracking-[0.15em] text-white/30 border-b border-white/[0.06] w-[200px]">
              Feature
            </th>
            <th className="text-center py-3.5 px-5 text-[11px] font-bold uppercase tracking-[0.15em] text-white/30 border-b border-white/[0.06]">
              {tool}
            </th>
            <th className="text-center py-3.5 px-5 text-[11px] font-bold uppercase tracking-[0.15em] text-brand-1 bg-brand-1/[0.04] border-b border-white/[0.06]">
              Tavlo
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.feature}
              className={`transition-colors hover:bg-white/[0.02] ${
                i < rows.length - 1 ? "border-b border-white/[0.04]" : ""
              }`}
            >
              <td className="py-3 px-5 text-[13px] font-semibold text-white/60">
                {row.feature}
              </td>
              <td className="py-3 px-5">
                <div className="flex items-center justify-center gap-2">
                  <StatusIcon status={row.tool} />
                  <span className="text-[13px] text-zinc-400">
                    {statusLabel(row.tool)}
                  </span>
                </div>
              </td>
              <td className="py-3 px-5 bg-brand-1/[0.04]">
                <div className="flex items-center justify-center gap-2">
                  <StatusIcon status={row.tavlo} />
                  <span
                    className={`text-[13px] ${
                      row.tavlo === "yes"
                        ? "text-white font-semibold"
                        : "text-zinc-400"
                    }`}
                  >
                    {statusLabel(row.tavlo)}
                  </span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-white/[0.06]">
            <td colSpan={3} className="py-3 px-5">
              <div className="flex justify-end">
                <Link
                  href="/sign-in"
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-1 hover:text-brand-1/80 transition-colors"
                >
                  Try Tavlo for Free
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function MobileCards({ tool, rows }: QuickCompareProps) {
  return (
    <div className="md:hidden space-y-2.5">
      {rows.map((row) => (
        <div
          key={row.feature}
          className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden"
        >
          <div className="px-4 py-2.5 bg-white/[0.03] border-b border-white/[0.04]">
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/40">
              {row.feature}
            </span>
          </div>
          <div className="divide-y divide-white/[0.04]">
            <div className="flex items-center justify-between px-4 py-2.5">
              <span className="text-[13px] font-medium text-white/40">
                {tool}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-zinc-400">
                  {statusLabel(row.tool)}
                </span>
                <StatusIcon status={row.tool} />
              </div>
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 bg-brand-1/[0.04]">
              <span className="text-[13px] font-medium text-brand-1">
                Tavlo
              </span>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[13px] ${
                    row.tavlo === "yes"
                      ? "text-white font-semibold"
                      : "text-zinc-400"
                  }`}
                >
                  {statusLabel(row.tavlo)}
                </span>
                <StatusIcon status={row.tavlo} />
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-center pt-1">
        <Link
          href="/sign-in"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-brand-1 hover:text-brand-1/80 transition-colors"
        >
          Try Tavlo for Free
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}

export function QuickCompare({ tool, rows }: QuickCompareProps) {
  return (
    <div className="my-12 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 sm:p-6">
      <div className="mb-4">
        <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/30">
          Quick Comparison
        </span>
        <h3 className="mt-1 text-lg font-bold text-white/90">
          {tool} vs. Tavlo
        </h3>
      </div>

      <DesktopTable tool={tool} rows={rows} />
      <MobileCards tool={tool} rows={rows} />
    </div>
  );
}

export default QuickCompare;
